/// <reference types="@types/google.maps" />
import { useState, useEffect, useRef } from 'react';
import {
  APIProvider,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';
import { Input } from '@/components/ui/input';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

// Helper to normalize UK postcode
function normalizePostcode(postcode: string): string {
  let clean = postcode.toUpperCase().replace(/\s+/g, '');
  // Insert space before the last 3 characters (inward code)
  if (clean.length >= 5) {
    clean = clean.slice(0, -3) + ' ' + clean.slice(-3);
  }
  return clean;
}

interface AddressData {
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  placeId?: string;
}

interface AddressPickerProps {
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  onAddressChange: (data: Partial<AddressData>) => void;
  error?: string;
}

export function AddressPicker(props: AddressPickerProps) {
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <AddressPickerInner {...props} />
    </APIProvider>
  );
}

function AddressPickerInner({
  addressLine1,
  addressLine2,
  city,
  postcode,
  onAddressChange,
  error,
}: AddressPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary('places');
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [localAddressLine1, setLocalAddressLine1] = useState(addressLine1);
  
  // Use ref to store latest callback to avoid recreating autocomplete on every render
  const onAddressChangeRef = useRef(onAddressChange);
  useEffect(() => {
    onAddressChangeRef.current = onAddressChange;
  }, [onAddressChange]);

  // Update local state when prop changes (but not during initial mount)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setLocalAddressLine1(addressLine1);
  }, [addressLine1]);

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const autocompleteInstance = new places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: 'gb' },
      fields: ['formatted_address', 'address_components', 'place_id'],
      types: ['address'],
    });

    autocompleteInstance.addListener('place_changed', () => {
      const place = autocompleteInstance.getPlace();
      if (place.address_components) {
        // Parse address components
        let streetNumber = '';
        let route = '';
        let subpremise = '';
        let locality = '';
        let postalTown = '';
        let adminAreaLevel2 = '';
        let extractedPostcode = '';

        for (const component of place.address_components) {
          const types = component.types;
          if (types.includes('subpremise')) {
            subpremise = component.long_name;
          } else if (types.includes('street_number')) {
            streetNumber = component.long_name;
          } else if (types.includes('route')) {
            route = component.long_name;
          } else if (types.includes('locality')) {
            locality = component.long_name;
          } else if (types.includes('postal_town')) {
            postalTown = component.long_name;
          } else if (types.includes('administrative_area_level_2')) {
            adminAreaLevel2 = component.long_name;
          } else if (types.includes('postal_code')) {
            extractedPostcode = component.long_name;
          }
        }

        // Build address line 1 (street number + route, or subpremise + street number + route)
        let line1 = '';
        if (subpremise) {
          line1 = subpremise + ', ';
        }
        if (streetNumber && route) {
          line1 += `${streetNumber} ${route}`;
        } else if (route) {
          line1 += route;
        }

        // City: prefer postal_town, then locality, then admin area level 2
        const extractedCity = postalTown || locality || adminAreaLevel2 || '';

        // Normalize postcode
        const normalizedPostcode = extractedPostcode 
          ? normalizePostcode(extractedPostcode)
          : '';

        setLocalAddressLine1(line1);
        
        // Use ref to call latest callback without causing useEffect to re-run
        onAddressChangeRef.current({
          addressLine1: line1,
          addressLine2: '', // Clear line 2, user can add if needed
          city: extractedCity,
          postcode: normalizedPostcode,
          placeId: place.place_id,
        });
      }
    });

    autocompleteRef.current = autocompleteInstance;

    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [places]); // Only depend on places, not onAddressChange

  return (
    <div className="space-y-4">
      {/* Address Line 1 with Autocomplete */}
      <div>
        <label className="block text-sm font-medium text-brand-950 mb-1.5">
          Address Line 1
        </label>
        <input
          ref={inputRef}
          type="text"
          value={localAddressLine1}
          placeholder="Start typing your address..."
          className="w-full px-4 py-3 rounded-xl border border-surface-200 bg-white text-brand-950 placeholder:text-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-colors"
          onChange={(e) => {
            setLocalAddressLine1(e.target.value);
            onAddressChange({ addressLine1: e.target.value });
          }}
        />
        <p className="mt-1 text-xs text-surface-600">
          💡 Start typing and select from suggestions to auto-fill your address
        </p>
        {error && <p className="mt-2 text-sm text-error-600">{error}</p>}
      </div>

      {/* Address Line 2 */}
      <Input
        label="Address Line 2 (optional)"
        placeholder="Flat, unit, building, floor, etc."
        value={addressLine2}
        onChange={(e) => onAddressChange({ addressLine2: e.target.value })}
      />

      {/* City and Postcode */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Input
          label="Town / City"
          placeholder="e.g., Birmingham"
          value={city}
          onChange={(e) => onAddressChange({ city: e.target.value })}
        />
        <Input
          label="Postcode"
          placeholder="e.g., B1 1AA"
          value={postcode}
          onChange={(e) => onAddressChange({ postcode: e.target.value })}
        />
      </div>
    </div>
  );
}
